import React, { Component } from "react";
import { PageHeader, Row, Col, Card, Statistic, Spin, Empty, Table, Typography, Select, message } from "antd";
import {
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  MessageOutlined,
  CloseCircleOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { Form, FormItem, ButtonItem, RangePickerItem, SelectItem } from "../../../Components/Items";
import TabbedDynamicTable from "../../../Components/TabbedDynamicTable";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import * as XLSX from "xlsx";
import moment from "moment";
import "moment/locale/es";
import { FASES_COLOR_HEX, SOPORTE_TEMA, OPCIONES_TIPO_INCIDENCIA, textoSobreFase } from "../../Panel/Soporte/Chat/fasesSoporte";
import {
  ESTILOS_REPORTE,
  NIVO_TEMA_SOPORTE,
  CARD_KPI_STYLE,
  CardGrafica,
  ACENTOS,
} from "../../Panel/Soporte/Tickets/estilosReporteSoporte";
import CardPuntosHistoria from "../../Panel/Soporte/Tickets/CardPuntosHistoria";

moment.locale("es");

const { Text } = Typography;
const { Option } = Select;

const TOPE_BARRAS = 12;

// Colores de las series: "atendido" es resuelto + subido, así que va en verde.
const COLOR_ATENDIDOS = FASES_COLOR_HEX.resuelto;
const COLOR_SUBIDOS = FASES_COLOR_HEX.subido;

// Los reportes de soporte se resuelven casi siempre el mismo día: en días
// saldrían todos como "0.4", así que se muestran en horas hasta las 24.
export const formatearDuracion = (horas) => {
  if (horas === null || horas === undefined) return "—";
  if (horas < 1) return `${Math.round(horas * 60)} min`;
  if (horas < 24) return `${horas} h`;
  return `${Math.round((horas / 24) * 10) / 10} d`;
};

const VACIO = {
  kpis: {},
  porEmpresa: [],
  porReportero: [],
  porFase: [],
  porSemana: [],
};

class DashboardSoporte extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    datos: VACIO,
    empresas: [],
  };

  componentDidMount() {
    this.consultar({ periodo: [moment().startOf("year"), moment()] });
    this.consultarEmpresas();
  }

  consultarEmpresas = () => {
    axios
      .post(
        "empresasBiwo/selectTodasEmpresasBiwo",
        { valor: 1, pageSize: 500 },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        const lista = res.data?.data || res.data?.empresas || [];
        this.setState({ empresas: Array.isArray(lista) ? lista : [] });
      })
      .catch(() => {
        // Sin catálogo el filtro por empresa queda vacío; el dashboard igual sirve.
      });
  };

  corporativos = () => {
    const porMaster = new Map();
    this.state.empresas.forEach((e) => {
      const id = e.usuarioId ? String(e.usuarioId) : null;
      if (!id) return;
      if (!porMaster.has(id)) porMaster.set(id, { _id: id, nombre: e.corporativo || "Sin corporativo", empresas: 0 });
      porMaster.get(id).empresas++;
    });
    return [...porMaster.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  };

  consultar = async (values) => {
    const [inicio, fin] = values?.periodo || [];
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "soporte/metricasSoporte",
        {
          fechaInicio: inicio ? inicio.startOf("day").toISOString() : null,
          fechaFin: fin ? fin.endOf("day").toISOString() : null,
          empresaId: values?.empresaId || null,
          corporativoId: values?.corporativoId || null,
          tipoIncidencia: values?.tipoIncidencia || null,
        },
        { headers: this.props.headersToken },
      );
      if (res.data.success === 2) this.setState({ datos: res.data });
      else message.info(res.data.message || "Las métricas no se cargaron, intenta de nuevo.");
    } catch (error) {
      message.info("Revisa tu conexión e intenta de nuevo.");
    } finally {
      this.setState({ loading: false });
    }
  };

  descargarExcel = () => {
    const { kpis, porEmpresa, porReportero, porSemana, porFase } = this.state.datos;

    const hojaResumen = XLSX.utils.json_to_sheet([
      { Indicador: "Tickets en el periodo", Valor: kpis.totalTickets ?? 0 },
      { Indicador: "Abiertos hoy", Valor: kpis.abiertos ?? 0 },
      { Indicador: "Atendidos", Valor: kpis.atendidos ?? 0 },
      { Indicador: "Cerrados", Valor: kpis.rechazados ?? 0 },
      { Indicador: "Empresas que reportaron", Valor: kpis.empresasReportando ?? 0 },
      { Indicador: "Resolución promedio", Valor: formatearDuracion(kpis.horasPromedioResolucion) },
      { Indicador: "Resolución mediana", Valor: formatearDuracion(kpis.horasMedianaResolucion) },
      { Indicador: "Tickets en la muestra de resolución", Valor: kpis.ticketsConResolucion ?? 0 },
      { Indicador: "1a respuesta promedio", Valor: formatearDuracion(kpis.horasPromedioPrimeraRespuesta) },
      // Story Point Real de Jira, crudo: no se ajusta a la escala Fibonacci.
      { Indicador: "Puntos promedio por ticket (escala 1-21)", Valor: kpis.puntosPromedio ?? "—" },
      { Indicador: "Puntos mediana", Valor: kpis.puntosMediana ?? "—" },
      { Indicador: "Tickets calificados por desarrollo", Valor: kpis.ticketsConPuntos ?? 0 },
      { Indicador: "Puntos totales del periodo", Valor: kpis.puntosTotales ?? 0 },
      { Indicador: "Confirmados por el cliente", Valor: kpis.confirmadosPorCliente ?? 0 },
      { Indicador: "Marcados como no cumplidos", Valor: kpis.noCumplidosPorCliente ?? 0 },
    ]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, hojaResumen, "Resumen");
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        porEmpresa.map((e) => ({
          Empresa: e.nombre,
          Corporativo: e.nombreCorporativo || "",
          Total: e.total,
          Abiertos: e.abiertos,
          Atendidos: e.atendidos,
          Cerrados: e.rechazados,
        })),
      ),
      "Por empresa",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        porReportero.map((r) => ({ Usuario: r.nombre, Correo: r.correo || "", Empresa: r.empresa || "", Reportes: r.total })),
      ),
      "Quien reporta",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        porSemana.map((s) => ({ Semana: s.semana, Inicio: s.etiqueta, Abiertos: s.abiertos, Atendidos: s.atendidos })),
      ),
      "Por semana",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(porFase.map((f) => ({ Fase: f.label, Total: f.total }))),
      "Por fase",
    );

    XLSX.writeFile(wb, `Metricas_Soporte_${moment().format("DD-MM-YYYY")}.xlsx`);
  };

  render() {
    const { loading, datos, empresas } = this.state;
    const { kpis, porEmpresa, porReportero, porFase, porSemana } = datos;
    const hayDatos = (kpis.totalTickets || 0) > 0;
    const corporativos = this.corporativos();

    const empresasGrafica = porEmpresa.slice(0, TOPE_BARRAS);
    const reporterosGrafica = porReportero.slice(0, 15);

    return (
      <>
        <style>{ESTILOS_REPORTE}</style>
        <PageHeader
          title="Métricas de Soporte"
          subTitle="Estadísticas del soporte técnico"
          onBack={() => this.props.history.push("/panelPrincipal/ticketsSoporte")}
          extra={
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* El botón vive fuera del <Form>, así que el submit se dispara por ref. */}
              <ButtonItem
                className="botonAgregar"
                type="primary"
                loading={loading}
                onClick={() => this.formRef.current?.submit()}
              >
                Consultar
              </ButtonItem>
              <ButtonItem
                className="botonAgregar"
                verde
                icon={<FileExcelOutlined />}
                disabled={!hayDatos}
                onClick={this.descargarExcel}
              >
                Excel
              </ButtonItem>
            </div>
          }
          style={{ border: "1px solid rgb(235, 237, 240)", backgroundColor: "white", marginBottom: 10 }}
        />

        <div className="rep-root">
          <Form
            ref={this.formRef}
            onFinish={this.consultar}
            initialValues={{ periodo: [moment().startOf("year"), moment()] }}
          >
            <TabbedDynamicTable
              showContentBorder={false}
              tabs={[
                {
                  key: "1",
                  label: "Periodo",
                  forceRender: true,
                  customContent: (
                    <FormItem name="periodo" margin>
                      <RangePickerItem />
                    </FormItem>
                  ),
                },
                {
                  key: "2",
                  label: "Corporativo",
                  forceRender: true,
                  customContent: (
                    <FormItem name="corporativoId" margin>
                      <SelectItem placeholder="Todos los corporativos" allowClear noFocusSiguiente style={{ width: "90%" }}>
                        {corporativos.map((c) => (
                          <Option key={c._id} value={c._id}>
                            {`${c.nombre} (${c.empresas} empresa${c.empresas === 1 ? "" : "s"})`}
                          </Option>
                        ))}
                      </SelectItem>
                    </FormItem>
                  ),
                },
                {
                  key: "3",
                  label: "Empresa",
                  forceRender: true,
                  customContent: (
                    <FormItem name="empresaId" margin>
                      <SelectItem placeholder="Todas las empresas" allowClear noFocusSiguiente style={{ width: "90%" }}>
                        {empresas.map((e) => (
                          <Option key={e._id} value={e._id}>
                            {e.corporativo ? `${e.nombre} · ${e.corporativo}` : e.nombre}
                          </Option>
                        ))}
                      </SelectItem>
                    </FormItem>
                  ),
                },
                {
                  key: "4",
                  label: "Tipo de incidencia",
                  forceRender: true,
                  customContent: (
                    <FormItem name="tipoIncidencia" margin>
                      <SelectItem
                        placeholder="Todos los tipos"
                        allowClear
                        noFocusSiguiente
                        options={OPCIONES_TIPO_INCIDENCIA}
                        style={{ width: "90%" }}
                      />
                    </FormItem>
                  ),
                },
              ]}
            />
          </Form>

          <Spin spinning={loading}>
            {!hayDatos ? (
              <Empty description="No hay reportes en el periodo seleccionado" />
            ) : (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic title="Tickets en el periodo" value={kpis.totalTickets} prefix={<FileTextOutlined />} />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {kpis.empresasReportando} empresa{kpis.empresasReportando === 1 ? "" : "s"} reportaron
                      </Text>
                    </Card>
                  </Col>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      {/* "Hoy" y no "del periodo": es el conteo actual, no una serie. */}
                      <Statistic
                        title="Abiertos hoy"
                        value={kpis.abiertos}
                        prefix={<ClockCircleOutlined />}
                        valueStyle={{ color: SOPORTE_TEMA.primario }}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        pre-reporte / por hacer / en curso
                      </Text>
                    </Card>
                  </Col>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic
                        title="Resolución promedio"
                        value={formatearDuracion(kpis.horasPromedioResolucion)}
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: COLOR_ATENDIDOS }}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        mediana {formatearDuracion(kpis.horasMedianaResolucion)} · {kpis.ticketsConResolucion} de{" "}
                        {kpis.totalTickets} tickets
                      </Text>
                    </Card>
                  </Col>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic
                        title="1ª respuesta promedio"
                        value={formatearDuracion(kpis.horasPromedioPrimeraRespuesta)}
                        prefix={<MessageOutlined />}
                        valueStyle={{ color: "#1890ff" }}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {kpis.ticketsConPrimeraRespuesta} de {kpis.totalTickets} tickets con respuesta
                      </Text>
                    </Card>
                  </Col>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <CardPuntosHistoria
                      promedio={kpis.puntosPromedio}
                      mediana={kpis.puntosMediana}
                      ticketsConPuntos={kpis.ticketsConPuntos}
                      totalTickets={kpis.totalTickets}
                      etiquetaTicket="ticket"
                    />
                  </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic
                        title="Cerrados"
                        value={kpis.rechazados}
                        prefix={<CloseCircleOutlined />}
                        valueStyle={{ color: FASES_COLOR_HEX.rechazado }}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {kpis.totalTickets ? Math.round((kpis.rechazados / kpis.totalTickets) * 100) : 0}% de los reportes
                      </Text>
                    </Card>
                  </Col>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic title="Confirmados por el cliente" value={kpis.confirmadosPorCliente} valueStyle={{ color: "#52c41a" }} />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        dijeron que sí se cumplió
                      </Text>
                    </Card>
                  </Col>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic title="No cumplidos" value={kpis.noCumplidosPorCliente} valueStyle={{ color: "#fa541c" }} />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        el cliente dijo que no quedó
                      </Text>
                    </Card>
                  </Col>
                  <Col style={{ display: "flex", flex: "1 1 210px" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic title="Atendidos" value={kpis.atendidos} valueStyle={{ color: COLOR_SUBIDOS }} />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        llegaron a resuelto o subido
                      </Text>
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <CardGrafica
                      etiqueta="Tendencia"
                      titulo="Abiertos vs atendidos por semana"
                      pill="Por semana"
                      alto={300}
                      delay="0.06s"
                    >
                      <ResponsiveLine
                        data={[
                          {
                            id: "Abiertos",
                            data: porSemana.map((s) => ({ x: s.etiqueta, y: s.abiertos })),
                          },
                          {
                            id: "Atendidos",
                            data: porSemana.map((s) => ({ x: s.etiqueta, y: s.atendidos })),
                          },
                        ]}
                        margin={{ top: 20, right: 100, bottom: 50, left: 45 }}
                        xScale={{ type: "point" }}
                        yScale={{ type: "linear", min: 0, stacked: false }}
                        colors={[SOPORTE_TEMA.primario, COLOR_ATENDIDOS]}
                        pointSize={7}
                        pointBorderWidth={2}
                        pointBorderColor={{ from: "serieColor" }}
                        axisBottom={{ tickRotation: -35 }}
                        axisLeft={{ format: (v) => (Number.isInteger(v) ? v : "") }}
                        enableGridX={false}
                        useMesh
                        legends={[
                          {
                            anchor: "bottom-right",
                            direction: "column",
                            translateX: 90,
                            itemWidth: 80,
                            itemHeight: 20,
                            symbolSize: 10,
                            symbolShape: "circle",
                            itemTextColor: "#64748b",
                          },
                        ]}
                        theme={NIVO_TEMA_SOPORTE}
                      />
                    </CardGrafica>
                  </Col>

                  <Col xs={24} lg={10}>
                    <CardGrafica
                      etiqueta="Estados"
                      titulo="Distribución por fase"
                      pill="Periodo"
                      accent={ACENTOS.lila}
                      alto={300}
                      delay="0.12s"
                    >
                      <ResponsivePie
                        data={porFase.map((f) => ({ id: f.label, label: f.label, value: f.total, fase: f.fase }))}
                        margin={{ top: 20, right: 20, bottom: 50, left: 20 }}
                        innerRadius={0.6}
                        padAngle={1}
                        cornerRadius={3}
                        // Mismos colores que el tablero y las etiquetas de fase.
                        colors={({ data }) => FASES_COLOR_HEX[data.fase] || "#d9d9d9"}
                        enableArcLinkLabels={false}
                        arcLabelsTextColor={({ data }) => textoSobreFase(data.fase)}
                        borderWidth={0}
                        activeOuterRadiusOffset={7}
                        legends={[
                          {
                            anchor: "bottom",
                            direction: "row",
                            translateY: 40,
                            itemWidth: 78,
                            itemHeight: 16,
                            symbolSize: 9,
                            symbolShape: "circle",
                            itemsSpacing: 2,
                            itemTextColor: "#64748b",
                          },
                        ]}
                        theme={NIVO_TEMA_SOPORTE}
                      />
                    </CardGrafica>
                  </Col>

                  <Col xs={24} lg={14}>
                    <CardGrafica
                      etiqueta="Volumen"
                      titulo="Tickets por empresa"
                      pill={`Top ${Math.min(TOPE_BARRAS, empresasGrafica.length)}`}
                      accent={ACENTOS.verde}
                      alto={Math.max(240, empresasGrafica.length * 34)}
                      delay="0.18s"
                      footer={
                        <Table
                          size="small"
                          rowKey={(r) => r.empresaId || r.nombre}
                          dataSource={porEmpresa}
                          pagination={porEmpresa.length > 10 ? { pageSize: 10 } : false}
                          columns={[
                            {
                              title: "Empresa",
                              dataIndex: "nombre",
                              render: (nombre, r) => (
                                <div>
                                  {nombre}
                                  {r.nombreCorporativo && (
                                    <div style={{ fontSize: 11, color: SOPORTE_TEMA.textSecondary }}>
                                      {r.nombreCorporativo}
                                    </div>
                                  )}
                                </div>
                              ),
                            },
                            { title: "Total", dataIndex: "total", width: 70 },
                            { title: "Abiertos", dataIndex: "abiertos", width: 85 },
                            { title: "Atendidos", dataIndex: "atendidos", width: 90 },
                            { title: "Cerrados", dataIndex: "rechazados", width: 95 },
                          ]}
                        />
                      }
                    >
                      <ResponsiveBar
                        data={[...empresasGrafica].reverse().map((e) => ({
                          empresa: e.nombre,
                          Abiertos: e.abiertos,
                          Atendidos: e.atendidos,
                          Cerrados: e.rechazados,
                        }))}
                        keys={["Abiertos", "Atendidos", "Cerrados"]}
                        indexBy="empresa"
                        layout="horizontal"
                        margin={{ top: 10, right: 110, bottom: 40, left: 150 }}
                        padding={0.3}
                        colors={[SOPORTE_TEMA.primario, COLOR_ATENDIDOS, FASES_COLOR_HEX.rechazado]}
                        borderRadius={3}
                        axisBottom={{ format: (v) => (Number.isInteger(v) ? v : "") }}
                        labelSkipWidth={16}
                        enableGridY={false}
                        legends={[
                          {
                            dataFrom: "keys",
                            anchor: "bottom-right",
                            direction: "column",
                            translateX: 105,
                            itemWidth: 90,
                            itemHeight: 18,
                            symbolSize: 10,
                            symbolShape: "circle",
                            itemTextColor: "#64748b",
                          },
                        ]}
                        theme={NIVO_TEMA_SOPORTE}
                      />
                    </CardGrafica>
                  </Col>

                  <Col xs={24} lg={10}>
                    <CardGrafica
                      etiqueta="Ranking"
                      titulo="Quién reporta más"
                      pill={`Top ${Math.min(15, reporterosGrafica.length)}`}
                      accent={ACENTOS.mostaza}
                      alto={Math.max(240, reporterosGrafica.length * 30)}
                      delay="0.24s"
                    >
                      <ResponsiveBar
                        data={[...reporterosGrafica].reverse().map((r) => ({ usuario: r.nombre, Reportes: r.total }))}
                        keys={["Reportes"]}
                        indexBy="usuario"
                        layout="horizontal"
                        margin={{ top: 10, right: 30, bottom: 40, left: 160 }}
                        padding={0.3}
                        colors={[SOPORTE_TEMA.primario]}
                        borderRadius={3}
                        axisBottom={{ format: (v) => (Number.isInteger(v) ? v : "") }}
                        enableGridY={false}
                        theme={NIVO_TEMA_SOPORTE}
                      />
                    </CardGrafica>
                  </Col>
                </Row>
              </>
            )}
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
});

export default withRouter(connect(mapStateToProps)(DashboardSoporte));
